import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PrimaryBtn } from 'components/UI/Buttons';
import CloseableAlert from 'components/UI/CloseableAlert';
import LoadingOverlay from 'components/UI/LoadingOverlay';
import React, { type FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useCreateOnboardingMutation } from 'services/api/onboarding.api';
import getReCaptchaToken from 'utils/getReCaptchaToken';
import { identifyPosthogUser } from 'utils/posthogUtils';
import validateEmail from 'utils/validations/validateEmail';

import { signInUser } from '../../redux/slices/user.slice';

const OnboardingInsuranceBroker: FC = () => {
  const [createOnboarding, { data, isLoading, isSuccess, error }] = useCreateOnboardingMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [emailInput, setEmailInput] = React.useState('');
  const [invalidEmail, setInvalidEmail] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const handleGoBack = () => {
    navigate('/onboarding');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);

    const formData = new FormData(event.currentTarget);

    if (!validateEmail(emailInput)) {
      setInvalidEmail(true);
      setSubmitError('Por favor, ingresa un email válido');
      return;
    }

    try {
      const recaptchaToken = await getReCaptchaToken();

      const onboardingData = {
        recaptchaToken,
        companyName: formData.get('companyName')?.toString() || '',
        userName: formData.get('userName')?.toString() || '',
        email: emailInput,
        password: formData.get('password')?.toString() || '',
        phoneNumber: formData.get('phoneNumber')?.toString() || '',
        companyType: 'insurance_broker' as const,
      };

      await createOnboarding(onboardingData);
    } catch (_onboardingError) {
      setSubmitError('Error al procesar el registro. Por favor, intenta nuevamente.');
    }
  };

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(signInUser(data.user));
      identifyPosthogUser(data.user);
      navigate('/insurance-credentials');
    }
  }, [data, isSuccess, dispatch, navigate]);

  const errorMessage = error && 'data' in error ? String(error.data) : null;

  return (
    <>
      <LoadingOverlay visible={isLoading} />
      <Grid sx={{ minHeight: '110vh' }} container component="main">
        <Grid size={12} component={Paper} elevation={6} square>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: { xs: '100%', lg: '60%' },
              mx: 'auto',
              height: '100vh',
              alignItems: 'center',
              pt: '60px',
            }}
          >
            <img alt="Relif" src="/logo.webp" height="80" />

            <Box sx={{ width: '100%', maxWidth: 600, mb: 4, pt: '28px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Paso 2 de 2
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  100%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={100} sx={{ height: 8, borderRadius: 4 }} />
            </Box>

            <Typography variant="h4">Registro de Corredora de Seguros</Typography>
            <Typography variant="body1" sx={{ mt: 1, mb: 2, textAlign: 'center', color: 'text.secondary' }}>
              Completa los datos para crear tu cuenta de corredora de seguros y comenzar a usar Relif
            </Typography>

            {errorMessage && <CloseableAlert msg={errorMessage} severity="error" />}
            {submitError && <CloseableAlert msg={submitError} severity="error" />}

            <Box
              sx={{
                bgcolor: 'backgroundGrey.main',
                p: 3,
                borderRadius: '20px',
                mt: 2,
                border: '1px solid hsla(210,18%,87%,1)',
                width: { xs: '88%', lg: '450px' },
              }}
              component="form"
              onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                handleSubmit(e).catch((err) => {
                  console.error(err);
                });
              }}
            >
              <TextField
                id="companyName"
                margin="normal"
                required
                fullWidth
                label="Nombre de la Corredora"
                name="companyName"
                autoComplete="organization"
                autoFocus
                sx={{ bgcolor: '#fff' }}
              />

              <TextField
                id="userName"
                margin="normal"
                required
                fullWidth
                label="Tu Nombre"
                name="userName"
                autoComplete="name"
                sx={{ bgcolor: '#fff' }}
              />

              <TextField
                id="email"
                margin="normal"
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                sx={{ bgcolor: '#fff' }}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setInvalidEmail(false);
                }}
                helperText={invalidEmail ? 'Por favor, ingresa un email válido' : ''}
                error={invalidEmail}
              />

              <TextField
                id="password"
                margin="normal"
                required
                fullWidth
                label="Contraseña"
                name="password"
                type="password"
                autoComplete="new-password"
                sx={{ bgcolor: '#fff' }}
                helperText="Mínimo 12 caracteres, con mayúscula, minúscula, número y carácter especial"
              />

              <TextField
                id="phoneNumber"
                margin="normal"
                required
                fullWidth
                label="Teléfono de Contacto"
                name="phoneNumber"
                type="tel"
                autoComplete="tel"
                sx={{ bgcolor: '#fff' }}
                helperText="Número de teléfono de la corredora"
              />

              <PrimaryBtn sx={{ mt: 3, py: 1, mb: 1 }} type="submit" fullWidth variant="contained">
                Crear Cuenta
              </PrimaryBtn>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  ¿Ya tienes una cuenta?{' '}
                  <a href="/sign-in" style={{ color: 'inherit', textDecoration: 'underline' }}>
                    Iniciar Sesión
                  </a>
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={handleGoBack} variant="outlined" sx={{ mt: 2 }}>
                  Volver Atrás
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default OnboardingInsuranceBroker;
